import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v3';
import { type Account, accountApi } from '@/api/commercial/accounts';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateProposalFlow } from '@/context/create-proposal-flow';
import { useToast } from '@/hooks/use-toast';
import { isValidCNPJ, isValidCPF, maskCpfCnpj, maskRg } from '@/lib/formatters';
import { cn } from '@/lib/utils';

const schema = z
  .object({
    documentType: z.enum(['cpf', 'cnpj'], {
      required_error: 'Selecione o tipo de documento',
    }),
    document: z.string().min(1, 'Documento obrigatório'),
    name: z.string().min(1, 'Nome obrigatório'),
    social_name: z.string().optional(),
    fantasy_name: z.string().optional(),
    state_registration: z.string().optional(),
    municipal_registration: z.string().optional(),
    rg: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const cleanDoc = data.document.replace(/\D/g, '');

    if (data.documentType === 'cpf') {
      if (cleanDoc.length !== 11) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CPF deve ter 11 dígitos',
          path: ['document'],
        });
      } else if (!isValidCPF(cleanDoc)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CPF inválido',
          path: ['document'],
        });
      }
    } else {
      if (cleanDoc.length !== 14) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CNPJ deve ter 14 dígitos',
          path: ['document'],
        });
      } else if (!isValidCNPJ(cleanDoc)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CNPJ inválido',
          path: ['document'],
        });
      }
    }
  });

type AccountEditFormData = z.infer<typeof schema>;

interface AccountEditModalProps {
  open: boolean;
  onClose: () => void;
  account: Account | null;
  onSuccess?: (account: Account) => void;
}

export function AccountEditModal({ open, onClose, account, onSuccess }: AccountEditModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { setAccount: setProposalAccount } = useCreateProposalFlow();

  const {
    register,
    control,
    setValue,
    watch,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AccountEditFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      documentType: 'cnpj',
      document: '',
      name: '',
      social_name: '',
      fantasy_name: '',
      state_registration: '',
      municipal_registration: '',
      rg: '',
    },
  });

  const documentType = watch('documentType');

  // Reset form when account changes
  useEffect(() => {
    if (open && account) {
      reset({
        documentType: account.document?.type || 'cnpj',
        document: account.document?.value || '',
        name: account.name || '',
        social_name: account.social_name || '',
        fantasy_name: account.fantasy_name || '',
        state_registration: account.state_registration || '',
        municipal_registration: account.municipal_registration || '',
        rg: maskRg(account.rg || ''),
      });
    }
  }, [open, account, reset]);

  // Mutation for updating account
  const updateAccountMutation = useMutation({
    mutationFn: async (data: AccountEditFormData) => {
      if (!account?.id) throw new Error('Account ID is required');

      const accountData: Partial<Account> = {
        document: {
          type: data.documentType,
          value: data.document,
        },
        name: data.name,
        social_name: data.social_name,
        fantasy_name: data.fantasy_name,
        state_registration: data.state_registration,
        municipal_registration: data.municipal_registration,
        rg: data.rg,
      };

      await accountApi.update(account.id, accountData);

      // Fetch the complete account data after update
      const fullAccount = await accountApi.findOne(account.id);
      return fullAccount;
    },
    onSuccess: (fullAccount) => {
      toast({
        title: 'Conta atualizada',
        description: 'A conta foi atualizada com sucesso!',
      });

      // Update the context with the complete account data
      setProposalAccount(fullAccount);

      // Call the parent's success callback to update search results
      onSuccess?.(fullAccount);

      // Invalidate queries
      queryClient.invalidateQueries({
        queryKey: ['accounts'],
      });
      queryClient.invalidateQueries({
        queryKey: ['findOneAccount', fullAccount.id],
      });

      // Close modal
      onClose();
    },
    onError: (error) => {
      console.error('Error updating account:', error);
      toast({
        title: 'Erro',
        description: 'Ocorreu um erro ao atualizar a conta. Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: AccountEditFormData) => {
    updateAccountMutation.mutate(data);
  };

  const _handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = maskCpfCnpj(e.target.value);
    setValue('document', masked);
  };

  const isCnpj = documentType === 'cnpj';

  if (!account) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Conta</DialogTitle>
          <DialogDescription>
            Atualize os dados da conta. As alterações serão refletidas na proposta.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Tipo de Documento (read-only display) */}
          <div className="space-y-1.5">
            <Label>Tipo de Documento</Label>
            <Input
              value={isCnpj ? 'CNPJ (Pessoa Jurídica)' : 'CPF (Pessoa Física)'}
              disabled
              className="bg-muted"
            />
          </div>

          {/* Documento (read-only display - editing requires special permission) */}
          <div className="space-y-1.5">
            <Label>{isCnpj ? 'CNPJ' : 'CPF'}</Label>
            <Input
              value={maskCpfCnpj(account.document?.value || '')}
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              Para alterar o documento, entre em contato com o administrador.
            </p>
          </div>

          {/* Nome / Razão Social */}
          <div className="space-y-1.5">
            <Label htmlFor="name">{isCnpj ? 'Razão Social' : 'Nome Completo'} *</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder={isCnpj ? 'Razão Social da empresa' : 'Nome completo'}
              className={cn(errors.name && 'border-red-500')}
            />
            {errors.name && <p className="text-xs text-red-500">{errors.name.message}</p>}
          </div>

          {/* RG (apenas para CPF) */}
          {!isCnpj && (
            <div className="space-y-1.5">
              <Label htmlFor="rg">RG</Label>
              <Controller
                control={control}
                name="rg"
                render={({ field }) => (
                  <Input
                    id="rg"
                    value={field.value || ''}
                    onChange={(e) => field.onChange(maskRg(e.target.value))}
                    placeholder="RG (opcional)"
                  />
                )}
              />
            </div>
          )}

          {/* Campos específicos para CNPJ */}
          {isCnpj && (
            <>
              {/* Nome Fantasia */}
              <div className="space-y-1.5">
                <Label htmlFor="fantasy_name">Nome Fantasia</Label>
                <Input
                  id="fantasy_name"
                  {...register('fantasy_name')}
                  placeholder="Nome fantasia (opcional)"
                />
              </div>

              {/* Inscrição Estadual */}
              <div className="space-y-1.5">
                <Label htmlFor="state_registration">Inscrição Estadual</Label>
                <Input
                  id="state_registration"
                  {...register('state_registration')}
                  placeholder="Inscrição Estadual (opcional)"
                />
              </div>

              {/* Inscrição Municipal */}
              <div className="space-y-1.5">
                <Label htmlFor="municipal_registration">Inscrição Municipal</Label>
                <Input
                  id="municipal_registration"
                  {...register('municipal_registration')}
                  placeholder="Inscrição Municipal (opcional)"
                />
              </div>
            </>
          )}

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-black text-white hover:bg-gray-800"
              disabled={isSubmitting || updateAccountMutation.isPending}
            >
              {(isSubmitting || updateAccountMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Salvar Alterações
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
